import { useStore } from "effector-react"
import { array, nonEmptyArray } from "fp-ts"

import { $view } from "@/domains/auctions/store/list-view-mode"
import { styled } from "@/stitches"
import { Text } from "@/ui/text"

import { AuctionPreview, View } from "../model"
import { AuctionCard, AuctionCardProps, AuctionCardSkeleton } from "./card"

const Grid = styled("ul", {
  display: "grid",

  variants: {
    view: {
      grid: {
        gap: "$8",
        "@sm": {
          gridTemplateColumns: "repeat(auto-fill, minmax(25rem, 1fr))",
        },
      },
      gridCompact: {
        gap: "$8",
        "@sm": {
          gridTemplateColumns: "repeat(auto-fill, minmax(18rem, 1fr))",
        },
      },
      list: {
        gap: "$4",
        "& > li:not(:last-child)": {
          borderBottom: "1px solid $divider",
        },
      },
    },
  },

  defaultVariants: {
    view: "grid",
  },
})

export function AuctionGridSkeleton(
  props: AuctionsGridProps & { items?: number }
) {
  const { items = 8, ...gridProps } = props

  return (
    <Grid {...gridProps}>
      {nonEmptyArray.range(1, items).map((key) => (
        <AuctionCardSkeleton key={key} />
      ))}
    </Grid>
  )
}

export type AuctionsGridProps = {
  auctions?: Array<AuctionPreview> | null
  loading?: boolean
  loadingItems?: number
  emptyMessage?: string
  cardProps?: Omit<AuctionCardProps, "auction">
  view?: View
}

export function AuctionsGrid({
  auctions,
  view,
  cardProps,
  loading = false,
  loadingItems = 8,
  emptyMessage = "Žádné položky",
  ...props
}: AuctionsGridProps) {
  const globalView = useStore($view)
  const actualView = view ?? globalView
  const actualGridProps = { view: actualView, ...props }
  const actualCardProps = { view: actualView, ...cardProps }

  if (loading) {
    return <AuctionGridSkeleton {...actualGridProps} items={loadingItems} />
  }

  if (auctions && array.isNonEmpty(auctions)) {
    return (
      <Grid {...actualGridProps}>
        {auctions.map((auction) => (
          <li key={auction.id}>
            <AuctionCard {...actualCardProps} auction={auction} />
          </li>
        ))}
      </Grid>
    )
  }

  return (
    <Text as="p" size="xl" color="muted">
      {emptyMessage}
    </Text>
  )
}
